import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { listZGenerations, zGenerationReusePath, type ZStudioGeneration } from '@/lib/zstudio';

// Mini-panneau « dernières générations » (spec 56) — affiché directement dans
// les studios (Try-on, Lookbook, Édition IA) pour relancer une création en un
// clic sans repasser par le formulaire.

const TYPE_META: Record<string, { label: string; icon: string; className: string }> = {
  image: { label: 'Image', icon: 'ri-image-line', className: 'bg-accent-100 text-accent-700' },
  video: { label: 'Vidéo', icon: 'ri-film-line', className: 'bg-primary-100 text-primary-700' },
  campaign: { label: 'Campagne', icon: 'ri-rocket-line', className: 'bg-primary-100 text-primary-700' },
  ad_copy: { label: 'Texte pub', icon: 'ri-megaphone-line', className: 'bg-secondary-100 text-secondary-800' },
  hashtags: { label: 'Hashtags', icon: 'ri-hashtag', className: 'bg-secondary-100 text-secondary-800' },
  slogan: { label: 'Slogan', icon: 'ri-quill-pen-line', className: 'bg-secondary-100 text-secondary-800' },
  tryon: { label: 'Try-on', icon: 'ri-user-star-line', className: 'bg-accent-100 text-accent-700' },
  edit_image: { label: 'Édition IA', icon: 'ri-equalizer-2-line', className: 'bg-accent-100 text-accent-700' },
  edit: { label: 'Édition IA', icon: 'ri-equalizer-2-line', className: 'bg-accent-100 text-accent-700' },
  lookbook: { label: 'Lookbook', icon: 'ri-book-open-line', className: 'bg-secondary-100 text-secondary-800' },
};

function outputUrl(g: ZStudioGeneration): string | null {
  const out = g.output;
  if (out && typeof out === 'object') {
    const u = (out as { url?: unknown }).url;
    if (typeof u === 'string' && u) return u;
    const urls = (out as { urls?: unknown }).urls;
    if (Array.isArray(urls) && typeof urls[0] === 'string') return urls[0];
  }
  return null;
}

export default function ZRecentGenerations({
  title = 'Dernières générations',
  limit = 4,
}: {
  title?: string;
  limit?: number;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<ZStudioGeneration[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    listZGenerations(user.id, 40)
      .then((all) => { if (alive) setItems(all.slice(0, limit)); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user?.id, limit]);

  if (loading) {
    return (
      <div className="mt-4 bg-background-50 border border-background-200/70 rounded-xl p-3.5">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-accent-500"></i>Chargement des dernières générations…
        </div>
      </div>
    );
  }
  if (!user?.id || items.length === 0) return null;

  return (
    <div className="mt-4 bg-background-50 border border-background-200/70 rounded-xl p-3.5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-foreground-800 flex items-center gap-1.5">
          <i className="ri-history-line text-accent-500"></i>{title}
        </p>
        <Link to="/dashboard/zstudio/history" className="text-[11px] font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer">
          Tout voir<i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {items.map((g) => {
          const meta = TYPE_META[(g.type || '').toLowerCase()] || TYPE_META.image;
          const url = outputUrl(g);
          const isVideo = (g.type || '').toLowerCase().includes('video');
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => navigate(zGenerationReusePath(g))}
              className="group text-left rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-400 transition-colors cursor-pointer"
            >
              <div className="aspect-square bg-background-100 flex items-center justify-center overflow-hidden relative">
                {url ? (
                  isVideo
                    ? <video src={url} muted className="w-full h-full object-cover object-top" />
                    : <img src={url} alt={g.prompt || 'Génération'} className="w-full h-full object-cover object-top" />
                ) : (
                  <i className={`${meta.icon} text-xl text-foreground-400`}></i>
                )}
                <span className="absolute inset-x-0 bottom-0 bg-black/45 text-white text-[10px] font-medium py-1 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-refresh-line mr-0.5"></i>Relancer
                </span>
              </div>
              <div className="p-2">
                <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full ${meta.className}`}>
                  <i className={meta.icon}></i>{meta.label}
                </span>
                <p className="text-[10px] text-foreground-600 mt-1 line-clamp-2 break-words">{g.prompt || 'Sans description'}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}